import { captureException } from '@sentry/react';
import {
  differenceInHours,
  differenceInMinutes,
  format,
  isToday,
  isYesterday,
} from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../use-locale';
import { useDateFormat } from '../use-date-format';
import { parseDateValue } from '../utils/parse-date-value';

interface Options {
  useTimeAgo?: boolean;
  useTodayYesterday?: boolean;
  formatString?: string;
  useStartOfLocalDay?: boolean;
}

export const useFormatDate = () => {
  const { locale } = useLocale();
  const { t } = useTranslation();
  const { defaultFormat } = useDateFormat();

  const formatDate = useCallback(
    (
      dateValue: Date | string | null | undefined,
      {
        useTimeAgo = false,
        useTodayYesterday = false,
        formatString,
        useStartOfLocalDay = false,
      }: Options = {}
    ): string => {
      const dateFormat = formatString ?? defaultFormat;
      if (!dateValue) return '-';

      const parsedDate = parseDateValue(dateValue, { useStartOfLocalDay });

      if (useTimeAgo) {
        const now = new Date();
        const diffMinutes = differenceInMinutes(now, parsedDate);
        const diffHours = differenceInHours(now, parsedDate);

        if (diffMinutes < 1) {
          return t('words.aFewSecondsAgo');
        }
        if (diffMinutes < 60) {
          return `${diffMinutes} ${t('words.minutesAgo')}`;
        }
        if (diffHours === 1) {
          return `${diffHours} ${t('words.hourAgo')}`;
        }
        if (isToday(parsedDate)) {
          return `${diffHours} ${t('words.hoursAgo')}`;
        }
        if (isYesterday(parsedDate)) {
          return t('words.yesterday');
        }
      }

      if (useTodayYesterday) {
        if (isToday(parsedDate)) {
          return t('words.today');
        }
        if (isYesterday(parsedDate)) {
          return t('words.yesterday');
        }
      }

      try {
        return format(parsedDate, dateFormat, {
          locale,
        });
      } catch {
        const valueStr =
          dateValue instanceof Date
            ? dateValue.toISOString()
            : String(dateValue);
        captureException(
          new Error(
            `Handled error: formatDate could not format dateValue ${valueStr} so returned -`
          )
        );

        return '-';
      }
    },
    [locale, t, defaultFormat]
  );

  return formatDate;
};
